import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react'
import { SystemSettings } from '@/types'
import { settingsService } from '@/services/settingsService'
import { widgetCache, CACHE_KEYS } from '@/utils/widgetCache'
import { AUTH_CHANGED_EVENT } from '@/utils/authEvents'

interface SystemContextType {
  systemSettings: SystemSettings | null
  isLoading: boolean
  refreshSystemSettings: () => Promise<void>
}

const SystemContext = createContext<SystemContextType | undefined>(undefined)

interface SystemProviderProps {
  children: ReactNode
}

export const SystemProvider: React.FC<SystemProviderProps> = ({ children }) => {
  // 啟動時直接吃 cache → 首屏就有 softwareName，省一支 API 等待。
  // 舊版 cache 沒有 profileRequiredFields，直接沿用會讓必填星號一直是空的 → 視為失效重抓。
  const cachedRaw = widgetCache.get<SystemSettings>(CACHE_KEYS.softwareName)
  const cached = Array.isArray(cachedRaw?.value?.profileRequiredFields) ? cachedRaw : null
  const [systemSettings, setSystemSettings] = useState<SystemSettings | null>(
    cached?.value ?? null
  )
  const [isLoading, setIsLoading] = useState(!cached)

  const loadSystemSettings = useCallback(async () => {
    try {
      // 有 cache 時不顯示 spinner，背景靜默更新
      if (!widgetCache.get<SystemSettings>(CACHE_KEYS.softwareName)) {
        setIsLoading(true)
      }

      // 已登入時優先打需授權的 API：只有它會回 profileRequiredFields，
      // 少了這份清單，個人資料頁與夥伴表單的必填星號全部不會出現。
      if (localStorage.getItem('token')) {
        try {
          const settings = await settingsService.getSystemSettings()
          setSystemSettings(settings)
          widgetCache.set(CACHE_KEYS.softwareName, settings)
          return
        } catch (privateError) {
          console.warn('已登入用戶API載入失敗，改用公開API:', privateError)
        }
      }

      // 未登入（例如登入頁）或授權 API 失敗時，公開 API 只拿得到軟體名稱
      try {
        const publicSettings = await settingsService.getPublicSoftwareName()
        const next: SystemSettings = { softwareName: publicSettings.softwareName, profileRequiredFields: [] }
        setSystemSettings(next)
        widgetCache.set(CACHE_KEYS.softwareName, next)
        return
      } catch (publicError) {
        console.warn('公開API載入失敗:', publicError)
      }

      // 如果都失敗 + 沒 cache，使用預設值；有 cache 就維持 cache 內容
      if (!systemSettings) {
        setSystemSettings({ softwareName: 'AnyTime', profileRequiredFields: [] })
      }
    } catch (error) {
      console.warn('載入系統設定失敗:', error)
      if (!systemSettings) {
        setSystemSettings({ softwareName: 'AnyTime', profileRequiredFields: [] })
      }
    } finally {
      setIsLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refreshSystemSettings = useCallback(async () => {
    widgetCache.invalidate(CACHE_KEYS.softwareName)
    await loadSystemSettings()
  }, [loadSystemSettings])

  useEffect(() => {
    loadSystemSettings()
  }, [])

  // 登入 / 登出後重抓：未登入時拿到的設定沒有 profileRequiredFields，
  // 不重抓的話使用者要手動重整才看得到必填星號
  useEffect(() => {
    const handleAuthChange = () => {
      refreshSystemSettings()
    }
    window.addEventListener(AUTH_CHANGED_EVENT, handleAuthChange)
    return () => window.removeEventListener(AUTH_CHANGED_EVENT, handleAuthChange)
  }, [refreshSystemSettings])

  // useMemo 穩定化 context value，函式不變時不觸發消費端 re-render
  const value = useMemo<SystemContextType>(() => ({
    systemSettings,
    isLoading,
    refreshSystemSettings,
  }), [systemSettings, isLoading, refreshSystemSettings])

  return (
    <SystemContext.Provider value={value}>
      {children}
    </SystemContext.Provider>
  )
}

export const useSystem = (): SystemContextType => {
  const context = useContext(SystemContext)
  if (context === undefined) {
    throw new Error('useSystem must be used within a SystemProvider')
  }
  return context
}

export default SystemContext
